/**
 * Small polyfills for what PDF.js 6 calls but some browsers lack, installed before anything else runs
 * (src/main.ts, src/parsers/pdf.classic.worker.ts). iPhone, 2026-10-11 (iOS 26.6.2): every PDF failed
 * with "undefined is not a function (near '...e of t...')": PDF.js reads page text with
 * `for await (const value of readableStream)`, and that WebKit has no ReadableStream async iterator
 * (the legacy build doesn't polyfill it).
 */
type AsyncIterableStream = ReadableStream & { [Symbol.asyncIterator]?: unknown; values?: unknown };

export function installReadableStreamIterator(g: typeof globalThis = globalThis): boolean {
  const RS = g.ReadableStream as unknown as { prototype: AsyncIterableStream } | undefined;
  if (!RS || typeof RS.prototype[Symbol.asyncIterator] === 'function') return false;
  function values(this: ReadableStream, options?: { preventCancel?: boolean }): AsyncIterableIterator<unknown> {
    const reader = this.getReader();
    const preventCancel = options?.preventCancel === true;
    return {
      async next() {
        const r = await reader.read();
        if (r.done) reader.releaseLock();
        return r.done ? { done: true, value: undefined } : { done: false, value: r.value };
      },
      async return(value?: unknown) {
        if (!preventCancel) await reader.cancel(value).catch(() => undefined);
        reader.releaseLock();
        return { done: true, value };
      },
      [Symbol.asyncIterator]() {
        return this;
      },
    };
  }
  Object.defineProperty(RS.prototype, 'values', { value: values, writable: true, configurable: true });
  Object.defineProperty(RS.prototype, Symbol.asyncIterator, { value: values, writable: true, configurable: true });
  return true;
}

installReadableStreamIterator();
